/**
 * @owner @Appboy/growth-engineering
 */

import type { I18nT } from "@lib/dashboardI18n";
import { getIntlDisplayNames } from "@lib/getIntlDisplayNames";

import type { AllowedLocales } from "../../../../../locales";

interface CountryOption {
  label: string;
  value: string;
}

interface CountryInfo {
  country: string;
  code: string;
  flag: string;
  phoneCode: string;
  label: string;
}

const priorityCountriesByLocale: Record<string, string[]> = {
  en: ["US"], // US first for English only
  ja: ["JP"], // Japan first for Japanese only
  // All other locales: no priority countries, pure alphabetical
} as const;

const defaultCountryCode = "US" as const;

/**
 * Gets locale-specific collation options for string comparison
 */
const getCollatorOptions = (locale: string): Intl.CollatorOptions => {
  switch (locale) {
    case "ja":
      return {
        sensitivity: "accent" as const,
        ignorePunctuation: false,
        numeric: true,
        caseFirst: "false" as const,
      };
    case "ko":
      return {
        sensitivity: "accent" as const,
        ignorePunctuation: false,
        numeric: true,
        usage: "sort" as const,
      };
    default:
      return {
        sensitivity: "base" as const,
        ignorePunctuation: true,
        numeric: true,
      };
  }
};

const getTranslatedCountryName = (countryInfo: CountryInfo, locale: string): string => {
  try {
    // Use browser's built-in Intl.DisplayNames for proper country name localization
    const displayNames = getIntlDisplayNames({
      currentLocale: locale as AllowedLocales,
      type: "region",
    });
    return displayNames.of(countryInfo.code) || countryInfo.country;
  } catch {
    // Fallback to the original country name if localization fails
    return countryInfo.country;
  }
};

const getEnglishCountryName = (countryInfo: CountryInfo): string => {
  try {
    // Always get English country name for form submission
    const displayNames = getIntlDisplayNames({
      currentLocale: "en" as AllowedLocales,
      type: "region",
    });
    return displayNames.of(countryInfo.code) || countryInfo.country;
  } catch {
    // Fallback to the original country name if localization fails
    return countryInfo.country;
  }
};

export const getDefaultCountryCodeByLocale = (locale: string): string => {
  const priorityCountries = priorityCountriesByLocale[locale];
  return priorityCountries?.[0] || defaultCountryCode;
};

export const sortCountriesByLocale = (
  countryOptions: CountryOption[],
  locale: string,
  _translate?: I18nT<"country-labels">
): CountryOption[] => {
  // Only English and Japanese have priority countries
  const priorityCodes = priorityCountriesByLocale[locale] || [];

  // Convert priority country codes to English names for comparison (since values are in English)
  const priorityNames: string[] = [];
  if (priorityCodes.length > 0) {
    try {
      const displayNames = getIntlDisplayNames({
        currentLocale: "en" as AllowedLocales,
        type: "region",
      });
      priorityCodes.forEach((code) => {
        const englishName = displayNames.of(code);
        if (englishName) {
          priorityNames.push(englishName);
        }
      });
    } catch {
      // Fallback to English names
      priorityCodes.forEach((code) => {
        if (code === "US") {
          priorityNames.push("United States");
        }
        if (code === "JP") {
          priorityNames.push("Japan");
        }
      });
    }
  }

  const priority: CountryOption[] = [];
  const others: CountryOption[] = [];

  // Normalize locale for better sorting
  const normalizedLocale =
    {
      ja: "ja-JP",
      ko: "ko-KR",
      en: "en-US",
      fr: "fr-FR",
      de: "de-DE",
      es: "es-ES",
      "pt-br": "pt-BR",
    }[locale] || locale;

  countryOptions.forEach((option) => {
    if (priorityNames.includes(option.value)) {
      priority.push(option);
    } else {
      others.push(option);
    }
  });

  priority.sort((a, b) => {
    const aIndex = priorityNames.indexOf(a.value);
    const bIndex = priorityNames.indexOf(b.value);
    return aIndex - bIndex;
  });

  others.sort((a, b) => {
    const compareOptions = getCollatorOptions(locale);
    return a.label.localeCompare(b.label, normalizedLocale, compareOptions);
  });

  return [...priority, ...others];
};

export const sortCountryInfoByLocale = (countries: CountryInfo[], locale: string): CountryInfo[] => {
  // Only English and Japanese have priority countries
  const priorityList = priorityCountriesByLocale[locale] || [];

  const priority: CountryInfo[] = [];
  const others: CountryInfo[] = [];

  // Normalize locale for better sorting
  const normalizedLocale =
    {
      ja: "ja-JP",
      ko: "ko-KR",
      en: "en-US",
      fr: "fr-FR",
      de: "de-DE",
      es: "es-ES",
      "pt-br": "pt-BR",
    }[locale] || locale;

  countries.forEach((country) => {
    if (priorityList.includes(country.code)) {
      priority.push(country);
    } else {
      others.push(country);
    }
  });

  priority.sort((a, b) => {
    const aIndex = priorityList.indexOf(a.code);
    const bIndex = priorityList.indexOf(b.code);
    return aIndex - bIndex;
  });

  others.sort((a, b) => {
    const aTranslated = getTranslatedCountryName(a, locale);
    const bTranslated = getTranslatedCountryName(b, locale);

    const compareOptions = getCollatorOptions(locale);
    return aTranslated.localeCompare(bTranslated, normalizedLocale, compareOptions);
  });

  return [...priority, ...others];
};

export const createCountryOptions = (countries: CountryInfo[], locale: string): CountryOption[] => {
  const countryOptions = countries.map((country) => ({
    label: getTranslatedCountryName(country, locale), // Translated name for display
    value: getEnglishCountryName(country), // English name for form submission
  }));

  return sortCountriesByLocale(countryOptions, locale);
};
